import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useSettings, digits } from "@/context/SettingsContext";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import { Flame, Home as HomeIcon, Sparkles, Heart, Gift, ArrowRight, MessageCircle, Landmark, ShieldCheck, Truck } from "lucide-react";

/*
  Occasion landing page — one shared template for 5 curated collections.
  Route: /occasions/:slug   (e.g. /occasions/daily-pooja)
*/

const OCCASIONS = {
  "daily-pooja": {
    icon: Flame,
    title: "Daily Pooja",
    tagline: "Ritual, refined.",
    subtitle: "Handpicked essentials for your everyday sacred practice — thalis, diyas, chambus and pooja bowls that turn each morning aarti into a moment of stillness.",
    heroImage: "/products/thali-1.jpg",
    keywords: ["daily pooja", "pooja thali", "diya", "chambu", "pooja bowl", "kumkum", "aarti"],
    categories: ["pooja-items"],
    ctaLabel: "Shop Daily Pooja",
  },
  "housewarming": {
    icon: HomeIcon,
    title: "Housewarming",
    tagline: "Bless a new beginning.",
    subtitle: "Auspicious pieces for griha pravesh and new home ceremonies — kalash, ganesh-lakshmi-saraswati idols and complete pooja combos to invite prosperity in.",
    heroImage: "/products/lakshmi-ganesh-1.jpg",
    keywords: ["housewarming", "griha pravesh", "kalash", "chambu", "idol", "complete set", "new home"],
    categories: ["housewarming", "pooja-items"],
    ctaLabel: "Shop Housewarming",
  },
  "festivals": {
    icon: Sparkles,
    title: "Festivals",
    tagline: "Light up every occasion.",
    subtitle: "Premium villakus, peacock deepams and traditional brass diyas — designed to bring warmth and elegance to Deepavali, Varalakshmi Vratham and every festive mandap.",
    heroImage: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/73blf55p_IMG_20260819_224404%5B1%5D.jpg",
    keywords: ["deepam", "villaku", "diya", "lamp", "peacock", "festival", "deepak", "varalakshmi"],
    categories: ["pooja-items"],
    ctaLabel: "Shop Festival Edit",
  },
  "wedding-auspicious": {
    icon: Heart,
    title: "Wedding & Auspicious Occasions",
    tagline: "Pieces that mark a moment.",
    subtitle: "Heirloom-quality German silver combos, complete pooja sets and elegant idols — ideal for weddings, engagements, first-year anniversaries and every auspicious muhurat.",
    heroImage: "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/2ki4p1ay_Image-89024%5B1%5D.jpg",
    keywords: ["varalakshmi", "wedding", "auspicious", "complete set", "combo", "idol set", "ganesh lakshmi"],
    categories: ["pooja-items", "housewarming"],
    ctaLabel: "Shop Wedding Edit",
  },
  "premium-gifting": {
    icon: Gift,
    title: "Premium Gifting",
    tagline: "Gift a piece of tradition.",
    subtitle: "Gift-ready sets in German silver, brass and antique finishes — thoughtful, timeless and crafted with care.",
    heroImage: "/products/thali-2.jpg",
    keywords: ["gift", "gifting", "spiritual gift", "combo", "set", "premium"],
    categories: ["gift-collection", "spiritual-products", "pooja-items"],
    ctaLabel: "Shop Gift Edit",
  },
};

export default function Occasion() {
  const { slug } = useParams();
  const config = OCCASIONS[slug];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  useEffect(() => {
    if (!config) return;
    (async () => {
      setLoading(true);
      // Fetch across the configured categories and filter locally by keyword match.
      const all = [];
      for (const cat of config.categories) {
        try {
          const { data } = await api.get("/products", { params: { category: cat, limit: 60 } });
          all.push(...(data.items || []));
        } catch {}
      }
      const dedup = Array.from(new Map(all.map((p) => [p.product_id, p])).values());
      const kws = config.keywords.map((k) => k.toLowerCase());
      const scored = dedup.map((p) => {
        const bag = [p.name, p.slug, p.subcategory, (p.tags || []).join(" ")].join(" ").toLowerCase();
        const score = kws.reduce((n, k) => n + (bag.includes(k) ? 1 : 0), 0);
        return { p, score };
      });
      scored.sort((a, b) => b.score - a.score || (b.p.is_best_seller ? 1 : 0) - (a.p.is_best_seller ? 1 : 0));
      setProducts(scored.filter((s) => s.score > 0).map((s) => s.p).slice(0, 24));
      setLoading(false);
    })();
  }, [slug, config]);

  if (!config) return <Navigate to="/shop" replace />;

  const Icon = config.icon;
  const waNumber = digits(settings.whatsapp_number);
  const waMsg = encodeURIComponent(`Hello Adiyogi Handicrafts, I'm shopping for ${config.title.toLowerCase()} — please share your recommendations.`);
  const waLink = waNumber ? `https://wa.me/${waNumber}?text=${waMsg}` : null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${config.title} · Adiyogi Handicrafts`,
    description: config.subtitle,
  };

  return (
    <div data-testid={`occasion-page-${slug}`}>
      <SEO
        title={`${config.title} — Curated Collection | Adiyogi Handicrafts`}
        description={config.subtitle}
        schema={schema}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-black text-ivory" data-testid="occasion-hero">
        <div className="absolute inset-0 bg-center bg-cover kenburns-3" style={{ backgroundImage: `url(${config.heroImage})` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 220px rgba(184,134,11,0.28)" }} />
        <div className="relative container-x py-20 md:py-28 min-h-[440px] md:min-h-[540px] flex items-center">
          <div className="max-w-2xl fade-in-slow">
            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground/80 mb-4"><Link to="/" className="hover:gold-text">Home</Link> · Occasions · <span className="text-[#E8C878]">{config.title}</span></div>
            <div className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.45em] text-[#E8C878] mb-4">
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {config.tagline}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
              <span className="gold-text italic">{config.title}</span>
            </h1>
            <p className="mt-5 text-sm md:text-lg text-ivory/85 max-w-xl">{config.subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#occasion-products" className="btn-gold rounded-full px-7 py-3 text-xs uppercase tracking-widest">{config.ctaLabel}</a>
              {waLink && <a href={waLink} target="_blank" rel="noreferrer noopener" className="rounded-full px-7 py-3 text-xs uppercase tracking-widest border border-[#E8C878]/60 text-ivory hover:bg-white/10 transition inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Ask on WhatsApp
              </a>}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST RIBBON */}
      <section className="bg-espresso text-ivory">
        <div className="container-x py-3 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 text-center">
          <span className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#E8C878]">
            <Landmark className="w-3.5 h-3.5" strokeWidth={1.5} /> A Karnataka Government Initiative
          </span>
          <span className="hidden md:inline text-ivory/40">·</span>
          <span className="text-[11px] md:text-sm text-ivory/85">EPCH Awarded · Prepaid UPI · Pan-India &amp; Worldwide Shipping</span>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section id="occasion-products" className="container-x py-16 md:py-24" data-testid="occasion-products-grid">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] gold-text mb-2">The Edit</div>
            <h2 className="font-serif text-3xl md:text-5xl">{config.title} Collection</h2>
          </div>
          <Link to="/shop" className="text-sm gold-text hidden md:flex items-center gap-2">
            All products <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
        {loading ? (
          <div className="text-center text-muted-foreground py-10 text-sm">Loading the edit…</div>
        ) : products.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="occasion-products-list">
            {products.map((p) => (<ProductCard key={p.product_id} product={p} />))}
          </div>
        ) : (
          <div className="max-w-md mx-auto text-center border hairline rounded-xl p-8 bg-white">
            <div className="font-serif text-lg mb-2">Curating this collection.</div>
            <div className="text-xs text-muted-foreground mb-5">
              We&apos;re handpicking the best {config.title.toLowerCase()} pieces. In the meantime, browse our full range or ping us on WhatsApp for a personalised suggestion.
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              <Link to="/shop" className="btn-outline-gold rounded-full px-5 py-2 text-xs uppercase tracking-widest">Browse All Products</Link>
              {waLink && <a href={waLink} target="_blank" rel="noreferrer" className="btn-gold rounded-full px-5 py-2 text-xs uppercase tracking-widest">Ask on WhatsApp</a>}
            </div>
          </div>
        )}
      </section>

      {/* WHY THIS EDIT */}
      <section className="bg-muted/40 border-y hairline">
        <div className="container-x py-14 md:py-20 grid md:grid-cols-3 gap-6">
          {[
            { icon: Sparkles,    title: "Hand-Curated",    body: `Every piece in this ${config.title.toLowerCase()} edit is chosen for craftsmanship and traditional design integrity.` },
            { icon: ShieldCheck, title: "Prepaid UPI",     body: "Guest checkout · Government-recognised handicrafts brand · No account needed to buy." },
            { icon: Truck,       title: "Ships Everywhere", body: "Pan-India in 3–4 days · Worldwide 10–21 days · Temple-grade packaging every time." },
          ].map((w) => (
            <div key={w.title} className="border hairline rounded-xl p-6 bg-white">
              <div className="w-10 h-10 rounded-full bg-[#B8860B]/10 flex items-center justify-center gold-text mb-3">
                <w.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div className="font-serif text-lg leading-tight mb-1">{w.title}</div>
              <div className="text-sm text-muted-foreground">{w.body}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
